import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import TestimonialForm from './TestimonialForm';
import { TestimonialApi } from '../../api/adminApi';
import './TestimonialsAdmin.css';

const TestimonialsAdmin = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [filterInput, setFilterInput] = useState('');
  const [pagePathFilter, setPagePathFilter] = useState('');

  const normalizePagePath = (value) => {
    if (!value) return '';
    let path = value.trim();
    if (!path) return '';
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
      path = path.replace(/\/+/g, '/');
      if (path.length > 1) {
        path = path.replace(/\/+$/, '');
      }
    if (!path) return '/';
    return path === '/' ? '/' : path.toLowerCase();
  };

  const loadTestimonials = async (pathFilter = pagePathFilter) => {
    setLoading(true);
    setError('');
    try {
      const normalized = normalizePagePath(pathFilter);
      const data = await TestimonialApi.getAll(normalized || undefined);
      setTestimonials(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTestimonials(pagePathFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePathFilter]);

  const handleApplyFilter = (event) => {
    event.preventDefault();
    setPagePathFilter(filterInput.trim());
  };

  const handleClearFilter = () => {
    setFilterInput('');
    setPagePathFilter('');
  };

  const handleCreate = () => {
    setCurrentTestimonial(null);
    setIsFormOpen(true);
  };

  const handleEdit = (testimonial) => {
    setCurrentTestimonial(testimonial);
    setIsFormOpen(true);
  };

  const handleDelete = async (testimonial) => {
    const confirmed = window.confirm(`Delete testimonial from "${testimonial.name}"?`);
    if (!confirmed) return;
    try {
      await TestimonialApi.remove(testimonial.id);
      await loadTestimonials();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to delete testimonial');
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setCurrentTestimonial(null);
  };

  const handleSubmit = async (payload) => {
    setIsSaving(true);
    try {
      if (currentTestimonial) {
        await TestimonialApi.update(currentTestimonial.id, payload);
      } else {
        await TestimonialApi.create(payload);
      }
      await loadTestimonials();
      closeForm();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to save testimonial');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    closeForm();
  };

  const renderTable = () => {
    if (loading) {
      return <p>Loading testimonials...</p>;
    }

    if (!testimonials.length) {
      return <p>No testimonials found.</p>;
    }

    return (
      <table className="testimonials-table">
        <thead>
          <tr>
            <th>Client</th>
            <th>Project</th>
            <th>Rating</th>
            <th>Order</th>
            <th>Page Paths</th>
            <th>Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {testimonials.map((testimonial) => (
            <tr key={testimonial.id}>
              <td>
                <div className="client-cell">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.handle}</span>
                </div>
              </td>
              <td>{testimonial.project || '-'}</td>
              <td>{testimonial.rating || '-'}</td>
              <td>{typeof testimonial.sortOrder === 'number' ? testimonial.sortOrder : 0}</td>
              <td>{testimonial.pageAliases?.length ? testimonial.pageAliases.join(', ') : 'All pages'}</td>
              <td>{testimonial.updatedAt ? new Date(testimonial.updatedAt).toLocaleDateString() : '-'}</td>
              <td>
                <div className="table-actions">
                  <button onClick={() => handleEdit(testimonial)}>Edit</button>
                  <button className="danger" onClick={() => handleDelete(testimonial)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <AdminLayout
      title="Testimonials"
      description="Collect, curate, and organize testimonials across each service page."
      actions={!isFormOpen && (
        <button className="admin-primary-btn" onClick={handleCreate}>+ Add Testimonial</button>
      )}
    >
      {error && <div className="admin-alert">{error}</div>}

      {!isFormOpen && (
        <form className="testimonial-filters" onSubmit={handleApplyFilter}>
          <div className="filter-group">
            <label htmlFor="pageFilter">Filter by page path</label>
            <input
              id="pageFilter"
              value={filterInput}
              onChange={(event) => setFilterInput(event.target.value)}
              placeholder="/services/app-development"
            />
          </div>
          <div className="filter-actions">
            <button type="submit" className="admin-primary-btn">Apply</button>
            <button type="button" className="admin-secondary-btn" onClick={handleClearFilter}>
              Clear
            </button>
          </div>
        </form>
      )}

      {isFormOpen ? (
        <TestimonialForm
          initialData={currentTestimonial}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSaving={isSaving}
        />
      ) : (
        <div className="testimonials-table-wrapper">{renderTable()}</div>
      )}
    </AdminLayout>
  );
};

export default TestimonialsAdmin;
